import {
    onMounted, onUnmounted, type Ref,
} from 'vue';

type UseNavigationScrollOptions = {
    isRail: Ref<boolean>;
};

const MAX_LAYOUT_RETRIES = 3;

export const useNavigationScroll = (options: UseNavigationScrollOptions) => {
    const { isRail } = options;
    let observer: MutationObserver | null = null;
    let lastScrolledHref: string | null = null;

    const scrollToActiveItem = (activeItem: Element, retryCount = 0) => {
        if (isRail.value) {
            return;
        }

        const drawer = document.querySelector('.primary-menu .v-navigation-drawer__content');
        if (!drawer) {
            return;
        }

        const drawerRect = drawer.getBoundingClientRect();
        const itemRect = activeItem.getBoundingClientRect();

        // Element not yet laid out - wait for next frame
        if (itemRect.height === 0 && retryCount < MAX_LAYOUT_RETRIES) {
            requestAnimationFrame(() => scrollToActiveItem(activeItem, retryCount + 1));

            return;
        }

        const isVisible = itemRect.top >= drawerRect.top && itemRect.bottom <= drawerRect.bottom;

        if (!isVisible) {
            activeItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const setupObserver = () => {
        const drawer = document.querySelector('.primary-menu .v-navigation-drawer__content');
        if (!drawer) {
            return;
        }

        observer = new MutationObserver(mutations => {
            for (const mutation of mutations) {
                if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') {
                    continue;
                }

                const target = mutation.target;
                if (!(target instanceof HTMLElement)) {
                    continue;
                }

                if (!target.classList.contains('v-list-item--active')) {
                    continue;
                }

                if (!target.matches('a.v-list-item')) {
                    continue;
                }

                const href = target.getAttribute('href');
                if (href === lastScrolledHref) {
                    continue;
                }

                lastScrolledHref = href;
                scrollToActiveItem(target);
            }
        });

        observer.observe(drawer, {
            attributes: true,
            attributeFilter: ['class'],
            subtree: true,
        });

        // Initial scroll for current active item
        const activeItem = drawer.querySelector('a.v-list-item--active');
        if (activeItem) {
            lastScrolledHref = activeItem.getAttribute('href');
            scrollToActiveItem(activeItem);
        }
    };

    onMounted(() => {
        setupObserver();
    });

    onUnmounted(() => {
        observer?.disconnect();
    });
};
